import {
  LayoutDashboard,
  Settings,
  FileText,
  Users,
  Phone,
  PhoneIncoming,
  UserPlus,
  BarChart3,
  GraduationCap,
  HelpCircle,
  Shield,
  CheckSquare,
  Zap,
  Mail,
  Building2,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { ThemeToggle } from './ThemeToggle';
import { useAuth } from '@/hooks/useAuth';
import { useActionCenterCounts } from '@/hooks/useActionCenterCounts';
import { Badge } from '@/components/ui/badge';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';

const mainItems = [
  {
    title: 'Dashboard',
    url: '/dashboard',
    icon: LayoutDashboard,
    memberUrl: '/portal/dashboard',
  },
  {
    title: 'Action Center',
    url: '/actions',
    icon: CheckSquare,
    hasNotifications: true,
    memberUrl: '/portal/dashboard',
  },
  {
    title: 'API Settings',
    url: '/api-settings',
    icon: Settings,
    memberUrl: '/portal/dashboard',
  },
];

const campaignItems = [
  {
    title: 'Campaigns',
    url: '/campaigns',
    icon: Phone,
    memberUrl: '/portal/dashboard',
  },
];

const managementItems = [
  {
    title: 'Script Management',
    url: '/script-management',
    icon: FileText,
    memberUrl: '/portal/dashboard',
  },
  {
    title: 'Prospect Management',
    url: '/prospects',
    icon: UserPlus,
    memberUrl: '/portal/dashboard',
  },
  {
    title: 'Business Portal',
    url: '/business-portal',
    icon: Building2,
    memberUrl: '/portal/dashboard',
  },
  {
    title: 'Clients',
    url: '/clients',
    icon: Users,
    memberUrl: '/portal/dashboard',
  },
  {
    title: 'Campaign Assignment',
    url: '/campaign-assignment',
    icon: Phone,
    memberUrl: '/portal/dashboard',
  },
];

const resourceItems = [
  {
    title: 'Training',
    url: '/training',
    icon: GraduationCap,
    memberUrl: '/portal/dashboard',
  },
  {
    title: 'Help',
    url: '/help',
    icon: HelpCircle,
    memberUrl: '/portal/dashboard',
  },
];

const adminItems = [
  { title: 'Users', url: '/admin/users', icon: Users },
  { title: 'Licenses', url: '/admin/licenses', icon: BarChart3 },
  { title: 'Tag Mapping', url: '/admin/tag-mapping', icon: Settings },
  { title: 'Billing', url: '/admin/billing', icon: BarChart3 },
  { title: 'API Usage Logs', url: '/admin/api-logs', icon: FileText },
  { title: 'Campaign Management', url: '/admin/campaigns', icon: Phone },
  { title: 'Script Templates', url: '/admin/script-templates', icon: FileText },
  { title: 'Action Manager', url: '/admin/action-types', icon: Zap },
  { title: 'Message Center', url: '/admin/message-center', icon: Mail },
  {
    title: 'Training Editor',
    url: '/admin/training-editor',
    icon: GraduationCap,
  },
  {
    title: 'Help Center Editor',
    url: '/admin/help-center-editor',
    icon: HelpCircle,
  },
  { title: 'System Settings', url: '/admin/settings', icon: Settings },
  { title: 'QA Test Harness', url: '/admin/qa-test', icon: Shield },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const { isOnlyMember, isAdmin } = useAuth();
  const currentPath = location.pathname;
  const isCollapsed = state === 'collapsed';
  const { overdueCount, dueTodayCount, totalPendingCount, loading } =
    useActionCenterCounts();

  const isActive = (path: string) => currentPath === path;
  const getNavClass = (path: string) =>
    isActive(path)
      ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
      : 'hover:bg-sidebar-accent/50';

  const getNotificationBadge = () => {
    if (loading || totalPendingCount === 0) return null;

    const displayCount =
      totalPendingCount >= 20 ? '20+' : totalPendingCount.toString();

    return (
      <Badge
        variant={overdueCount > 0 ? 'destructive' : 'secondary'}
        className={`ml-auto h-5 min-w-5 px-1 text-xs ${
          overdueCount > 0
            ? '' // Use default destructive styling
            : 'bg-warning text-warning-foreground hover:bg-warning/80'
        }`}
      >
        {displayCount}
      </Badge>
    );
  };

  const getNavUrl = (item: { url: string; memberUrl?: string }) => {
    return isOnlyMember && !isAdmin
      ? item.memberUrl || '/portal/dashboard'
      : item.url;
  };

  return (
    <Sidebar className={isCollapsed ? 'w-14' : 'w-64'} collapsible='icon'>
      <SidebarContent className='pt-4'>
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel>Main</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={getNavUrl(item)}
                      className={getNavClass(getNavUrl(item))}
                    >
                      <item.icon className='h-4 w-4' />
                      {!isCollapsed && <span>{item.title}</span>}
                      {false &&
                        !isCollapsed &&
                        item.hasNotifications &&
                        getNotificationBadge()}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Campaign Management */}
        <SidebarGroup>
          <SidebarGroupLabel>Campaigns</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {campaignItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={getNavUrl(item)}
                      className={getNavClass(getNavUrl(item))}
                    >
                      <item.icon className='h-4 w-4' />
                      {!isCollapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Management Tools */}
        <SidebarGroup>
          <SidebarGroupLabel>Management</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {managementItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={getNavUrl(item)}
                      className={getNavClass(getNavUrl(item))}
                    >
                      <item.icon className='h-4 w-4' />
                      {!isCollapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Resources */}
        <SidebarGroup>
          <SidebarGroupLabel>Resources</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {resourceItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={getNavUrl(item)}
                      className={getNavClass(getNavUrl(item))}
                    >
                      <item.icon className='h-4 w-4' />
                      {!isCollapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin - Only show for admin users */}
        {isAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink to={item.url} className={getNavClass(item.url)}>
                        <item.icon className='h-4 w-4' />
                        {!isCollapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Theme Toggle */}
        <SidebarGroup className='mt-auto'>
          <SidebarGroupContent>
            {!isCollapsed ? (
              <div className='px-2'>
                <div className='text-xs font-medium text-muted-foreground mb-2'>
                  Theme
                </div>
                <ThemeToggle />
              </div>
            ) : (
              <div className='flex justify-center'>
                <ThemeToggle />
              </div>
            )}
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
